/**
 * FilesSection - Cloud file management for projects
 *
 * File references stored externally (Google Drive, Dropbox, etc.)
 * Linked to tasks, milestones, or project directly
 * Supports bidirectional sync with Google Drive
 */

import React, { useState, useMemo } from 'react';
import {
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileCode,
  FileSpreadsheet,
  Folder,
  ExternalLink,
  Plus,
  Search,
  Filter,
  Link2,
  User,
  Calendar,
  MoreVertical,
  Trash2,
  Edit2,
  Download,
  Eye,
  RefreshCw,
  Cloud,
  CloudOff,
  FolderOpen,
  Loader,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import type { ProjectFile, Task, CloudProvider } from '@business-assistant/shared/domain';
import type { SyncStatus } from '@business-assistant/shared/services';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    overflow: 'hidden',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e5e5',
    background: '#fafafa',
  } as React.CSSProperties,
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '14px',
  } as React.CSSProperties,
  addBtn: {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,
  toolbar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e5e5',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    flex: 1,
    minWidth: '200px',
  } as React.CSSProperties,
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    flex: 1,
    background: 'transparent',
  } as React.CSSProperties,
  filterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
  } as React.CSSProperties,
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    padding: '16px',
  } as React.CSSProperties,
  fileCard: {
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  } as React.CSSProperties,
  fileCardHover: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderColor: '#2563eb',
  } as React.CSSProperties,
  thumbnail: {
    height: '100px',
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1px solid #e5e5e5',
  } as React.CSSProperties,
  thumbnailImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,
  fileInfo: {
    padding: '10px 12px',
  } as React.CSSProperties,
  fileName: {
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  fileMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11px',
    color: '#888',
  } as React.CSSProperties,
  providerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
  } as React.CSSProperties,
  linkedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    background: '#e0e7ff',
    borderRadius: '4px',
    fontSize: '10px',
    color: '#3b5998',
  } as React.CSSProperties,
  listView: {
    padding: '0',
  } as React.CSSProperties,
  listRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
    gap: '12px',
  } as React.CSSProperties,
  listIcon: {
    width: '36px',
    height: '36px',
    background: '#f5f5f5',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  listInfo: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  listActions: {
    display: 'flex',
    gap: '4px',
  } as React.CSSProperties,
  actionBtn: {
    background: 'none',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    color: '#666',
  } as React.CSSProperties,
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#888',
  } as React.CSSProperties,
  syncBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: '#e8f4ea',
    borderBottom: '1px solid #c8e6c9',
    fontSize: '13px',
  } as React.CSSProperties,
  syncBarDisconnected: {
    background: '#fff8e1',
    borderBottom: '1px solid #ffecb3',
  } as React.CSSProperties,
  syncInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#137333',
  } as React.CSSProperties,
  syncInfoDisconnected: {
    color: '#f57c00',
  } as React.CSSProperties,
  syncBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    background: '#137333',
    color: '#fff',
  } as React.CSSProperties,
  syncBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  } as React.CSSProperties,
  openFolderBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    border: '1px solid #137333',
    background: 'transparent',
    color: '#137333',
  } as React.CSSProperties,
  headerActions: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  lastSync: {
    fontSize: '11px',
    color: '#666',
  } as React.CSSProperties,
};

// =============================================================================
// TYPES
// =============================================================================

export interface FilesSectionProps {
  files: ProjectFile[];
  tasks?: Task[];
  onAddFile?: () => void;
  onOpenFile?: (file: ProjectFile) => void;
  onDeleteFile?: (id: string) => void;
  onEditFile?: (id: string) => void;
  // Drive sync props
  isConnectedToDrive?: boolean;
  driveFolderId?: string;
  syncStatus?: SyncStatus | null;
  isSyncing?: boolean;
  onSync?: () => Promise<void>;
  onOpenDriveFolder?: () => void;
}

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'images' | 'documents' | 'spreadsheets' | 'other';

// =============================================================================
// HELPERS
// =============================================================================

const PROVIDER_COLORS: Record<CloudProvider, { bg: string; color: string }> = {
  'google-drive': { bg: '#e8f4ea', color: '#137333' },
  dropbox: { bg: '#e3f2fd', color: '#0061ff' },
  onedrive: { bg: '#fff3e0', color: '#d83b01' },
  s3: { bg: '#fff8e1', color: '#ff9800' },
  other: { bg: '#f5f5f5', color: '#666' },
};

const PROVIDER_LABELS: Record<CloudProvider, string> = {
  'google-drive': 'Drive',
  dropbox: 'Dropbox',
  onedrive: 'OneDrive',
  s3: 'S3',
  other: 'Link',
};

function getFileIcon(mimeType: string): React.ReactNode {
  if (mimeType.startsWith('image/')) return <FileImage size={24} color="#4caf50" />;
  if (mimeType.startsWith('video/')) return <FileVideo size={24} color="#9c27b0" />;
  if (mimeType.startsWith('audio/')) return <FileAudio size={24} color="#ff9800" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <FileSpreadsheet size={24} color="#2e7d32" />;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word'))
    return <FileText size={24} color="#d32f2f" />;
  if (
    mimeType.includes('javascript') ||
    mimeType.includes('json') ||
    mimeType.includes('typescript')
  )
    return <FileCode size={24} color="#795548" />;
  return <File size={24} color="#607d8b" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// formatDate is now handled inside the component with locale awareness

function getFileTypeFilter(mimeType: string): FilterType {
  if (mimeType.startsWith('image/')) return 'images';
  if (
    mimeType.includes('pdf') ||
    mimeType.includes('document') ||
    mimeType.includes('word') ||
    mimeType.includes('text')
  )
    return 'documents';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheets';
  return 'other';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FilesSection({
  files,
  tasks = [],
  onAddFile,
  onOpenFile,
  onDeleteFile,
  onEditFile,
  isConnectedToDrive = false,
  driveFolderId,
  syncStatus,
  isSyncing = false,
  onSync,
  onOpenDriveFolder,
}: FilesSectionProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Locale-aware date formatting
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatLastSync = (dateStr?: string) => {
    if (!dateStr) return t('files.neverSynced');
    const date = new Date(dateStr);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (diffMinutes < 1) return t('files.justNow');
    if (diffMinutes < 60) return t('files.minutesAgo', { count: diffMinutes });
    if (diffMinutes < 1440) return t('files.hoursAgo', { count: Math.floor(diffMinutes / 60) });
    return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  };

  // Build task lookup
  const taskMap = useMemo(() => {
    const map = new Map<string, Task>();
    tasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [tasks]);

  // Filter files
  const filteredFiles = useMemo(() => {
    return files.filter((f) => {
      // Search filter
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Type filter
      if (filterType !== 'all' && getFileTypeFilter(f.mimeType) !== filterType) {
        return false;
      }
      return true;
    });
  }, [files, search, filterType]);

  // Sort by date (newest first)
  const sortedFiles = useMemo(() => {
    return [...filteredFiles].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [filteredFiles]);

  const handleOpenExternal = (file: ProjectFile) => {
    window.open(file.externalUrl, '_blank');
  };

  const renderFileCard = (file: ProjectFile) => {
    const isHovered = hoveredCard === file.id;
    const linkedTask = file.linkedToId ? taskMap.get(file.linkedToId) : null;
    const providerStyle = PROVIDER_COLORS[file.provider];

    return (
      <div
        key={file.id}
        style={{
          ...styles.fileCard,
          ...(isHovered ? styles.fileCardHover : {}),
        }}
        onMouseEnter={() => setHoveredCard(file.id)}
        onMouseLeave={() => setHoveredCard(null)}
        onClick={() => onOpenFile?.(file)}
      >
        {/* Thumbnail */}
        <div style={styles.thumbnail}>
          {file.thumbnailUrl ? (
            <img src={file.thumbnailUrl} alt={file.name} style={styles.thumbnailImg} />
          ) : (
            getFileIcon(file.mimeType)
          )}
        </div>

        {/* Info */}
        <div style={styles.fileInfo}>
          <div style={styles.fileName} title={file.name}>
            {file.name}
          </div>
          <div style={styles.fileMeta}>
            <span
              style={{
                ...styles.providerBadge,
                background: providerStyle.bg,
                color: providerStyle.color,
              }}
            >
              {PROVIDER_LABELS[file.provider]}
            </span>
            <span>{formatFileSize(file.size)}</span>
          </div>
          {linkedTask && (
            <div style={{ marginTop: '6px' }}>
              <span style={styles.linkedChip}>
                <Link2 size={10} />
                {linkedTask.title.slice(0, 20)}
                {linkedTask.title.length > 20 ? '...' : ''}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFileRow = (file: ProjectFile) => {
    const linkedTask = file.linkedToId ? taskMap.get(file.linkedToId) : null;
    const providerStyle = PROVIDER_COLORS[file.provider];

    return (
      <div key={file.id} style={styles.listRow}>
        <div style={styles.listIcon}>{getFileIcon(file.mimeType)}</div>

        <div style={styles.listInfo}>
          <div style={styles.fileName}>{file.name}</div>
          <div style={styles.fileMeta}>
            <span
              style={{
                ...styles.providerBadge,
                background: providerStyle.bg,
                color: providerStyle.color,
              }}
            >
              {PROVIDER_LABELS[file.provider]}
            </span>
            <span>{formatFileSize(file.size)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <Calendar size={10} />
              {formatDate(file.createdAt)}
            </span>
            {linkedTask && (
              <span style={styles.linkedChip}>
                <Link2 size={10} />
                {linkedTask.title.slice(0, 15)}...
              </span>
            )}
          </div>
        </div>

        <div style={styles.listActions}>
          <button
            style={styles.actionBtn}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenExternal(file);
            }}
            title={t('files.open')}
          >
            <ExternalLink size={16} />
          </button>
          <button
            style={styles.actionBtn}
            onClick={(e) => {
              e.stopPropagation();
              onEditFile?.(file.id);
            }}
            title={t('files.edit')}
          >
            <Edit2 size={16} />
          </button>
          <button
            style={{ ...styles.actionBtn, color: '#dc2626' }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFile?.(file.id);
            }}
            title={t('files.delete')}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <Folder size={16} />
          {t('files.title')} ({files.length})
        </div>
        <div style={styles.headerActions}>
          {onAddFile && (
            <button onClick={onAddFile} style={styles.addBtn}>
              <Plus size={14} />
              {t('files.add')}
            </button>
          )}
        </div>
      </div>

      {/* Drive Sync Bar */}
      <div
        style={{
          ...styles.syncBar,
          ...(isConnectedToDrive ? {} : styles.syncBarDisconnected),
        }}
      >
        <div
          style={{
            ...styles.syncInfo,
            ...(isConnectedToDrive ? {} : styles.syncInfoDisconnected),
          }}
        >
          {isConnectedToDrive ? (
            <>
              <Cloud size={16} />
              <span>{t('files.syncedWithDrive')}</span>
              {syncStatus?.lastSyncAt && (
                <span style={styles.lastSync}>
                  • {formatLastSync(syncStatus.lastSyncAt)}
                </span>
              )}
            </>
          ) : (
            <>
              <CloudOff size={16} />
              <span>{t('files.connectDriveToSync')}</span>
            </>
          )}
        </div>
        {isConnectedToDrive && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {driveFolderId && onOpenDriveFolder && (
              <button
                style={styles.openFolderBtn}
                onClick={onOpenDriveFolder}
              >
                <FolderOpen size={14} />
                {t('files.openFolder')}
              </button>
            )}
            {onSync && (
              <button
                style={{
                  ...styles.syncBtn,
                  ...(isSyncing ? styles.syncBtnDisabled : {}),
                }}
                onClick={onSync}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <>
                    <Loader
                      size={14}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                    {t('files.syncing')}
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} />
                    {t('files.sync')}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        {/* Search */}
        <div style={styles.searchBox}>
          <Search size={14} color="#888" />
          <input
            type="text"
            placeholder={t('files.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as FilterType)}
          style={styles.filterBtn}
        >
          <option value="all">{t('files.filters.all')}</option>
          <option value="images">{t('files.filters.images')}</option>
          <option value="documents">{t('files.filters.documents')}</option>
          <option value="spreadsheets">{t('files.filters.spreadsheets')}</option>
          <option value="other">{t('files.filters.other')}</option>
        </select>

        {/* View toggle */}
        <button
          style={{
            ...styles.filterBtn,
            background: viewMode === 'grid' ? '#e0e7ff' : '#fff',
          }}
          onClick={() => setViewMode('grid')}
        >
          {t('files.grid')}
        </button>
        <button
          style={{
            ...styles.filterBtn,
            background: viewMode === 'list' ? '#e0e7ff' : '#fff',
          }}
          onClick={() => setViewMode('list')}
        >
          {t('files.list')}
        </button>
      </div>

      {/* Content */}
      {files.length === 0 ? (
        <div style={styles.emptyState}>
          {isConnectedToDrive ? (
            <>
              <Cloud size={32} color="#137333" style={{ marginBottom: '8px' }} />
              <p style={{ margin: 0, color: '#137333' }}>
                {t('files.driveFolderReady')}
              </p>
              <p style={{ margin: '8px 0 12px', fontSize: '12px' }}>
                {t('files.uploadOrSync')}
              </p>
            </>
          ) : (
            <>
              <Folder size={32} color="#ccc" style={{ marginBottom: '8px' }} />
              <p style={{ margin: 0 }}>{t('files.noFiles')}</p>
            </>
          )}
          {onAddFile && (
            <button
              onClick={onAddFile}
              style={{
                ...styles.addBtn,
                margin: '12px auto 0',
                background: isConnectedToDrive ? '#137333' : '#666',
              }}
            >
              <Plus size={14} />
              {isConnectedToDrive ? t('files.uploadToDrive') : t('files.addFirst')}
            </button>
          )}
        </div>
      ) : sortedFiles.length === 0 ? (
        <div style={styles.emptyState}>
          <Search size={32} color="#ccc" style={{ marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>{t('common.noResults')}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={styles.grid}>{sortedFiles.map(renderFileCard)}</div>
      ) : (
        <div style={styles.listView}>{sortedFiles.map(renderFileRow)}</div>
      )}

      {/* Keyframes for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
